import React, { useCallback, useMemo, useState } from 'react';

import Icon from 'components/DesignSystem/Icon';
import OptionsPopover, { OptionsPopoverStatus } from 'components/Flux/Personalized/MyStudies/FilterBar/OptionsPopover';
import {
  buildCreatorOptions,
  creatorFilterName,
  creatorsQueryArgs,
} from 'helper/flux/myStudiesFilterOptions';
import { isValidUserId } from 'helper/flux/myStudyRow';
import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import useStudiesErrorState, { StudiesErrorKind } from 'utils/hooks/flux/personalized/useStudiesErrorState';
import {
  MyStudiesCompanyFilter,
  MyStudiesCreatorFilter,
  MyStudiesFiltersChange,
} from 'types/Flux/Personalized/myStudiesFilters';

import './index.scss';

/** Falha ao carregar os usuários, por tipo (`useStudiesErrorState`, modo `auxiliary`). */
const ERROR_MESSAGES: Readonly<Record<StudiesErrorKind, string>> = {
  network: 'Não foi possível carregar os usuários. Verifique sua conexão.',
  server: 'Não foi possível carregar os usuários. Tente de novo em instantes.',
  forbidden: 'Você não tem acesso à lista de usuários.',
};

/** Placeholder e nome da busca (.dc.html:210). */
const SEARCH_LABEL = 'Buscar usuário pelo nome';

export interface UserPopoverProps {
  /** `filters.criadoPor`: com `{ userId }`, o segmento fica ativo com o nome. */
  value: MyStudiesCreatorFilter;
  /** `filters.empresa`: o escopo da lista para admin (D16). */
  empresa: MyStudiesCompanyFilter;
  /** Papel de quem vê a tela: cliente fica sempre na própria empresa. */
  isAdmin: boolean;
  /**
   * Usuário logado (`selectUserId`), que não aparece na lista: ele é "Meus estudos". Sem usuário
   * válido (sem JWT), a lista não é pedida.
   */
  viewerUserId: number;
  /**
   * `setFilters` da tela (`useMyStudiesFilters`): recebe `{ criadoPor: { userId, nome } }`. O
   * nome vai junto para o chip e para a sessão; a URL só leva o id.
   */
  onFiltersChange: (change: MyStudiesFiltersChange) => void;
}

/**
 * Segmento "Usuário ▾" do Criado por (#2000, .dc.html:204-219), no `userSlot` do `CreatorFilter`:
 * fica dentro do grupo CRIADO POR, colado aos dois segmentos.
 *
 * - Botão: um `ds-segmented-option` do Segmented filled do DS (o wrapper do slot dá a altura, a
 *   borda e os cantos). "Usuário" vira "Usuário: <nome>" com um colega escolhido, e só então
 *   fica `active` (azul cheio = filtro ativo; aberto não muda a cor). O nome acessível leva o
 *   contexto, "Criado por Usuário: <nome>", para quem chega ao botão fora do grupo.
 * - Lista (D16): quem criou estudos no escopo, sem o próprio usuário; cada item mostra o nome e
 *   a empresa. Escopo: a empresa do filtro (admin) ou a própria (cliente, pelo JWT).
 * - Nome (ver `creatorFilterName`): o da lista vence o `nome` guardado no filtro.
 * - A lista só é pedida depois da 1ª abertura ou com um colega escolhido (para o nome). Fechar
 *   não solta a assinatura: reabrir não refaz o pedido, e um erro não gera outro evento no
 *   Sentry a cada reabertura. Trocar de empresa com um colega escolhido volta Criado por para
 *   Todos no `applyFilterChange`.
 * - Erros (#1984): `useStudiesErrorState(error, { mode: 'auxiliary' })`, mensagem na lista.
 *   Durante o "Tentar de novo" (`isFetching`), a mensagem volta a ser a de carregando.
 */
export default function UserPopover({
  value,
  empresa,
  isAdmin,
  viewerUserId,
  onFiltersChange,
}: UserPopoverProps) {
  const { api } = useMyStudiesProduct();
  const [open, setOpen] = useState(false);
  // Uma vez aberto, a lista fica assinada (ver o JSDoc)
  const [wasOpened, setWasOpened] = useState(false);
  const selectedId = typeof value === 'object' ? value.userId : null;
  const queryArgs = useMemo(() => creatorsQueryArgs(empresa, isAdmin), [empresa, isAdmin]);
  // `currentData`: a lista do escopo ATUAL; a de outra empresa nunca aparece no lugar
  const {
    currentData, error, isFetching, refetch,
  } = api.useCreatorsQuery(queryArgs, {
    skip: !isValidUserId(viewerUserId) || (!wasOpened && selectedId === null),
  });
  const errorKind = useStudiesErrorState(error, { mode: 'auxiliary' });

  const options = useMemo(
    () => (currentData ? buildCreatorOptions(currentData, viewerUserId) : []),
    [currentData, viewerUserId],
  );
  // O pedido do "Tentar de novo" não limpa o `error` (RTK Query 1.9): `isFetching` vence
  let status: OptionsPopoverStatus = 'loading';
  if (errorKind && !isFetching) status = 'error';
  else if (currentData) status = 'ready';

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setWasOpened(true);
  }, []);

  const name = creatorFilterName(value, currentData);
  const text = selectedId === null ? 'Usuário' : `Usuário: ${name ?? `#${selectedId}`}`;

  const handleSelect = useCallback((key: string) => {
    const chosen = options.find((option) => option.key === key);
    if (chosen) onFiltersChange({ criadoPor: { ...chosen.value } });
  }, [options, onFiltersChange]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <OptionsPopover
      open={open}
      onOpenChange={handleOpenChange}
      toggleAriaLabel={`Criado por ${text}`}
      toggleClassName={`ds-segmented-option my-studies-user-toggle${selectedId === null ? '' : ' active'}`}
      toggleContent={(
        <>
          <span className="my-studies-user-toggle-label">{text}</span>
          <span className="my-studies-user-toggle-chevron" aria-hidden="true">
            <Icon iconType="down_arrow" color="currentColor" size="small" />
          </span>
        </>
      )}
      align="end"
      menuClassName="my-studies-user-menu"
      searchLabel={SEARCH_LABEL}
      searchPlaceholder={SEARCH_LABEL}
      listLabel="Usuários"
      options={options}
      selectedKey={selectedId === null ? null : String(selectedId)}
      onSelect={handleSelect}
      status={status}
      loadingMessage="Carregando usuários…"
      emptyMessage="Nenhum usuário encontrado."
      errorMessage={errorKind ? ERROR_MESSAGES[errorKind] : undefined}
      onRetry={errorKind === 'forbidden' ? undefined : retry}
    />
  );
}
